/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Synchronizes appointment mutations across ViewAppointments list/calendar state collections.
 * @module logic/appointments/appointmentStateUpdates
 */

/**
 * Applies an updater to every appointment collection in ViewAppointments state.
 * @param {string} appointmentId - Appointment identifier
 * @param {Function} updater - Receives the current appointment and returns the next value
 * @param {Object} setters - React state setters for each collection
 */
export const updateAppointmentInCollections = (
  appointmentId,
  updater,
  { setAppointments, setFilteredRequests },
) => {
  const mapCollection = (collection) =>
    collection.map((item) =>
      item._id === appointmentId ? updater(item) : item,
    );

  setAppointments((prev) => mapCollection(prev));
  setFilteredRequests((prev) => mapCollection(prev));
};

export const markAppointmentAsSeenInCollections = (appointmentId, setters) => {
  updateAppointmentInCollections(
    appointmentId,
    (appointment) => ({ ...appointment, estado: "seen" }),
    setters,
  );
};

export const applyRescheduledAppointmentInCollections = (
  appointmentId,
  schedule,
  setters,
) => {
  updateAppointmentInCollections(
    appointmentId,
    (appointment) => ({
      ...appointment,
      ...schedule,
      confirmado: false,
    }),
    setters,
  );
};

export const applyVirtualAppointmentInCollections = (
  appointmentId,
  updatedAppointment,
  setters,
) => {
  updateAppointmentInCollections(
    appointmentId,
    (appointment) => ({ ...appointment, ...updatedAppointment }),
    setters,
  );
};

/**
 * Marks approved/rejected appointments as confirmed across all ViewAppointments collections.
 * @param {Set<string>} confirmedAppointmentIds
 * @param {Object} setters
 */
export const applyConfirmedAppointmentsInCollections = (
  confirmedAppointmentIds,
  setters,
) => {
  const markConfirmed = (appointment) => {
    if (
      appointment.estado === "approved" ||
      appointment.estado === "rejected"
    ) {
      return { ...appointment, confirmado: true };
    }
    if (confirmedAppointmentIds.has(appointment._id)) {
      return { ...appointment, confirmado: true };
    }
    return appointment;
  };

  setters.setAppointments((prev) => prev.map(markConfirmed));
  setters.setFilteredRequests((prev) => prev.map(markConfirmed));
};
